/**
 * Filter Status Component
 * Displays active filters and provides status indicators
 */

import { useFilterStore } from '../stores/filterStore.js';
import { notificationSystem } from './NotificationSystem.js';

/**
 * Filter Status Manager
 */
export class FilterStatusManager {
  constructor() {
    this.container = null;
    this.filterStore = useFilterStore;
    this.initialized = false;

    // Subscribe to filter store changes
    this.unsubscribe = this.filterStore.subscribe((state) => {
      this.updateDisplay(state);
    });
  }

  /**
   * Initialize the filter status display
   */
  init(containerId = 'filter-status-container') {
    if (this.initialized) return;

    this.container = document.getElementById(containerId);
    if (!this.container) {
      this.createContainer(containerId);
    }

    this.initialized = true;
    this.updateDisplay(this.filterStore.getState());
  }

  /**
   * Create container if it doesn't exist
   */
  createContainer(containerId) {
    this.container = document.createElement('div');
    this.container.id = containerId;
    this.container.className = 'filter-status-container';

    // Find appropriate place to insert (e.g., after search container)
    const searchContainer = document.getElementById('searchContainer');
    if (searchContainer && searchContainer.parentNode) {
      searchContainer.parentNode.insertBefore(this.container, searchContainer.nextSibling);
    } else {
      // Fallback: add to main content area
      const mainContent = document.querySelector('.main-content') || document.body;
      mainContent.appendChild(this.container);
    }
  }

  /**
   * Update the filter status display
   */
  updateDisplay(state) {
    if (!this.container) return;

    const activeFilters = this.getActiveFilters(state);
    const filterCount = activeFilters.length;

    if (filterCount === 0) {
      this.container.innerHTML = '';
      this.container.style.display = 'none';
      return;
    }

    this.container.style.display = 'block';
    this.container.innerHTML = this.renderFilterStatus(activeFilters, filterCount);
    this.attachEventListeners();
  }

  /**
   * Extract active filters from state
   */
  getActiveFilters(state) {
    const active = [];

    // Countries
    if (state.selectedCountries && state.selectedCountries.length > 0) {
      active.push({
        type: 'countries',
        label: 'Countries',
        value: state.selectedCountries,
        count: state.selectedCountries.length,
        displayValue: this.getCountriesDisplayValue(state.selectedCountries)
      });
    }

    // Commodities
    if (state.selectedCommodities && state.selectedCommodities.length > 0) {
      active.push({
        type: 'commodities',
        label: 'Commodities',
        value: state.selectedCommodities,
        count: state.selectedCommodities.length,
        displayValue: this.getCommoditiesDisplayValue(state.selectedCommodities)
      });
    }

    // Risk Levels
    if (state.selectedRiskLevels && state.selectedRiskLevels.length > 0 && state.selectedRiskLevels.length < 5) {
      active.push({
        type: 'riskLevels',
        label: 'Risk Levels',
        value: state.selectedRiskLevels,
        count: state.selectedRiskLevels.length,
        displayValue: this.getRiskLevelsDisplayValue(state.selectedRiskLevels)
      });
    }

    // Custom filters
    if (state.customFilters) {
      Object.keys(state.customFilters).forEach(key => {
        const value = state.customFilters[key];
        if (value !== null && value !== undefined && value !== '') {
          active.push({
            type: 'custom',
            label: this.formatFilterLabel(key),
            value: value,
            count: 1,
            displayValue: String(value)
          });
        }
      });
    }

    return active;
  }

  /**
   * Format display value for countries
   */
  getCountriesDisplayValue(countries) {
    if (countries.length <= 2) {
      return countries.join(', ');
    }
    return `${countries[0]} and ${countries.length - 1} more`;
  }

  /**
   * Format display value for commodities
   */
  getCommoditiesDisplayValue(commodities) {
    if (commodities.length <= 2) {
      return commodities.join(', ');
    }
    return `${commodities[0]} and ${commodities.length - 1} more`;
  }

  /**
   * Format display value for risk levels
   */
  getRiskLevelsDisplayValue(riskLevels) {
    const formatted = riskLevels.map(level =>
      level.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())
    );

    if (formatted.length <= 2) {
      return formatted.join(', ');
    }
    return `${formatted[0]} and ${formatted.length - 1} more`;
  }

  /**
   * Format filter label
   */
  formatFilterLabel(key) {
    return key.replace(/([A-Z])/g, ' $1')
             .replace(/^./, str => str.toUpperCase())
             .trim();
  }

  /**
   * Render the filter status HTML
   */
  renderFilterStatus(activeFilters, totalCount) {
    return `
      <div class="filter-status-header">
        <div class="filter-status-info">
          <span class="filter-status-icon">🔍</span>
          <span class="filter-status-text">
            ${totalCount} filter${totalCount > 1 ? 's' : ''} active
          </span>
        </div>
        <div class="filter-status-actions">
          <button class="filter-clear-all" title="Clear all filters">
            <span>Clear All</span>
            <span class="clear-icon">×</span>
          </button>
        </div>
      </div>
      <div class="filter-status-list">
        ${activeFilters.map(filter => this.renderFilterTag(filter)).join('')}
      </div>
    `;
  }

  /**
   * Render individual filter tag
   */
  renderFilterTag(filter) {
    const countBadge = filter.count > 1 ? `<span class="filter-count">${filter.count}</span>` : '';

    return `
      <div class="filter-tag" data-filter-type="${filter.type}">
        <span class="filter-label">${filter.label}</span>
        <span class="filter-value">${filter.displayValue}</span>
        ${countBadge}
        <button class="filter-remove" data-filter-type="${filter.type}" title="Remove ${filter.label} filter">
          ×
        </button>
      </div>
    `;
  }

  /**
   * Attach event listeners to filter status elements
   */
  attachEventListeners() {
    if (!this.container) return;

    // Clear all filters
    const clearAllButton = this.container.querySelector('.filter-clear-all');
    if (clearAllButton) {
      clearAllButton.addEventListener('click', () => {
        this.showClearAllConfirmation();
      });
    }

    // Remove individual filters
    const removeButtons = this.container.querySelectorAll('.filter-remove');
    removeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const filterType = e.target.getAttribute('data-filter-type');
        this.removeFilter(filterType);
      });
    });

    // Show filter details on tag click
    const filterTags = this.container.querySelectorAll('.filter-tag');
    filterTags.forEach(tag => {
      tag.addEventListener('click', (e) => {
        if (e.target.classList.contains('filter-remove')) return;
        const filterType = tag.getAttribute('data-filter-type');
        this.showFilterDetails(filterType);
      });
    });
  }

  /**
   * Show confirmation dialog for clearing all filters
   */
  showClearAllConfirmation() {
    const activeCount = this.getActiveFilters(this.filterStore.getState()).length;

    notificationSystem.show(
      `Remove all ${activeCount} active filters?`,
      'warning',
      {
        title: 'Clear All Filters',
        duration: 0,
        actions: [
          {
            action: 'cancel',
            label: 'Cancel',
            handler: (notificationId) => {
              notificationSystem.dismiss(notificationId);
            }
          },
          {
            action: 'confirm',
            label: 'Clear All',
            handler: (notificationId) => {
              this.clearAllFilters();
              notificationSystem.dismiss(notificationId);
              notificationSystem.showSuccess('All filters cleared', { duration: 2000 });
            }
          }
        ]
      }
    );
  }

  /**
   * Remove a specific filter
   */
  removeFilter(filterType) {
    const { clearCountries, clearCommodities, clearRiskLevels, clearCustomFilters } = this.filterStore.getState();

    switch (filterType) {
      case 'countries':
        clearCountries();
        notificationSystem.showInfo('Country filters cleared', { duration: 2000 });
        break;
      case 'commodities':
        clearCommodities();
        notificationSystem.showInfo('Commodity filters cleared', { duration: 2000 });
        break;
      case 'riskLevels':
        clearRiskLevels();
        notificationSystem.showInfo('Risk level filters cleared', { duration: 2000 });
        break;
      case 'custom':
        clearCustomFilters();
        notificationSystem.showInfo('Custom filters cleared', { duration: 2000 });
        break;
    }

    // Trigger filter application
    this.applyFiltersWithFeedback();
  }

  /**
   * Clear all filters
   */
  clearAllFilters() {
    const { resetFilters } = this.filterStore.getState();
    if (resetFilters) {
      resetFilters();
      this.applyFiltersWithFeedback();
    }
  }

  /**
   * Show detailed information about a filter
   */
  showFilterDetails(filterType) {
    const state = this.filterStore.getState();
    const activeFilters = this.getActiveFilters(state);
    const filter = activeFilters.find(f => f.type === filterType);

    if (!filter) return;

    let details = '';
    if (Array.isArray(filter.value)) {
      details = filter.value.join(', ');
    } else {
      details = String(filter.value);
    }

    notificationSystem.show(
      `${filter.label}: ${details}`,
      'info',
      {
        title: 'Filter Details',
        duration: 4000
      }
    );
  }

  /**
   * Apply filters with user feedback
   */
  applyFiltersWithFeedback() {
    // This would typically call the filter manager
    // For now, we'll emit a custom event
    const event = new CustomEvent('filtersChanged', {
      detail: { source: 'filter-status' }
    });
    document.dispatchEvent(event);
  }

  /**
   * Get filter statistics for display
   */
  getFilterStats() {
    const state = this.filterStore.getState();
    const activeFilters = this.getActiveFilters(state);

    return {
      totalActive: activeFilters.length,
      byType: activeFilters.reduce((acc, filter) => {
        acc[filter.type] = (acc[filter.type] || 0) + 1;
        return acc;
      }, {}),
      hasCountryFilters: activeFilters.some(f => f.type === 'countries'),
      hasCommodityFilters: activeFilters.some(f => f.type === 'commodities'),
      hasRiskLevelFilters: activeFilters.some(f => f.type === 'riskLevels'),
      hasCustomFilters: activeFilters.some(f => f.type === 'custom')
    };
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}

// Export singleton instance
export const filterStatusManager = new FilterStatusManager();
export const FilterStatus = FilterStatusManager; // Alias for component usage

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    filterStatusManager.init();
  });
} else {
  filterStatusManager.init();
}